import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Loader2, Gift, Clock, Search, Plus, Minus, Ban, Sparkles } from "lucide-react";
import { toast } from "sonner";

const ETYPE = {
  free: { label: "Free", cls: "bg-zinc-100 text-zinc-600" },
  trial: { label: "Trial", cls: "bg-emerald-100 text-emerald-700" },
  paid: { label: "Paid Pro", cls: "bg-indigo-100 text-indigo-700" },
  complimentary: { label: "Complimentary", cls: "bg-teal-100 text-teal-700" },
  lifetime: { label: "Lifetime", cls: "bg-amber-100 text-amber-700" },
  expired_trial: { label: "Expired trial", cls: "bg-rose-100 text-rose-700" },
};

export function AdminCreatorTrials() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [q, setQ] = useState("");
  const [sel, setSel] = useState({});
  const [cfg, setCfg] = useState({ creator_launch_trial_enabled: true, creator_trial_months: 3, creator_trial_offer_end: "" });
  const [savingCfg, setSavingCfg] = useState(false);
  const [confirmAction, setConfirmAction] = useState(null); // { kind, ids }
  const [busy, setBusy] = useState(false);

  const load = () => {
    setLoading(true);
    api.get("/admin/creator-trials").then((r) => { setData(r.data); setCfg(r.data.config); }).finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const saveCfg = async () => {
    setSavingCfg(true);
    try { await api.post("/admin/creator-trials/config", cfg); toast.success("Trial settings saved"); load(); }
    catch { toast.error("Could not save settings"); }
    finally { setSavingCfg(false); }
  };

  const selectedIds = Object.keys(sel).filter((k) => sel[k]);

  const run = async (kind, ids, extra = {}) => {
    setBusy(true);
    try {
      const path = kind === "grant" ? "/admin/creator-trials/grant"
        : kind === "revoke" ? "/admin/creator-trials/revoke" : "/admin/creator-trials/adjust";
      const r = await api.post(path, { user_ids: ids, confirm: true, ...extra });
      const d = r.data;
      const done = (d.granted || d.revoked || d.updated || []).length;
      const skipped = (d.skipped || []).length;
      toast.success(`${done} updated${skipped ? `, ${skipped} skipped` : ""}`);
      setSel({}); setConfirmAction(null); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Action failed"); }
    finally { setBusy(false); }
  };

  if (loading) return <div className="flex h-40 items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-emerald-600" /></div>;
  if (!data) return null;

  const rows = data.rows.filter((r) => (filter === "all" || r.entitlement_type === filter)
    && (!q || r.name.toLowerCase().includes(q.toLowerCase()) || r.email.toLowerCase().includes(q.toLowerCase())));

  return (
    <div data-testid="admin-creator-trials" className="mt-6 space-y-6">
      {/* Config */}
      <div className="rounded-2xl border border-zinc-200 bg-white p-5">
        <div className="flex items-center gap-2"><Sparkles className="h-5 w-5 text-emerald-600" /><h3 className="font-display text-lg font-bold text-zinc-900">Founding Creator Pro — automatic offer</h3></div>
        <div className="mt-4 grid gap-4 sm:grid-cols-3">
          <div className="flex items-center justify-between rounded-xl border border-zinc-200 p-3">
            <Label htmlFor="auto-trial" className="text-sm">Auto-grant to new creators</Label>
            <Switch id="auto-trial" data-testid="trial-auto-toggle" checked={!!cfg.creator_launch_trial_enabled} onCheckedChange={(v) => setCfg((c) => ({ ...c, creator_launch_trial_enabled: v }))} />
          </div>
          <div>
            <Label className="text-xs text-zinc-500">Duration (months)</Label>
            <Input data-testid="trial-months" type="number" min={1} max={24} value={cfg.creator_trial_months} onChange={(e) => setCfg((c) => ({ ...c, creator_trial_months: Number(e.target.value) }))} className="mt-1" />
          </div>
          <div>
            <Label className="text-xs text-zinc-500">Offer end date (optional)</Label>
            <Input data-testid="trial-offer-end" type="date" value={cfg.creator_trial_offer_end || ""} onChange={(e) => setCfg((c) => ({ ...c, creator_trial_offer_end: e.target.value }))} className="mt-1" />
          </div>
        </div>
        <p className="mt-2 text-xs text-zinc-500">New registrations after the offer end date won't auto-receive a trial. Existing trials keep running. Leave blank to keep the offer always-on.</p>
        <Button data-testid="save-trial-config" onClick={saveCfg} disabled={savingCfg} className="mt-3 rounded-full bg-emerald-600 hover:bg-emerald-700">{savingCfg ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save settings"}</Button>
      </div>

      {/* Counts */}
      <div className="flex flex-wrap gap-2">
        {Object.entries(data.counts).map(([k, v]) => (
          <span key={k} className={`rounded-full px-3 py-1 text-xs font-semibold ${ETYPE[k]?.cls || "bg-zinc-100 text-zinc-600"}`}>{ETYPE[k]?.label || k}: {v}</span>
        ))}
      </div>

      {/* Upcoming expirations */}
      {data.upcoming_expirations.length > 0 && (
        <div data-testid="upcoming-expirations" className="rounded-2xl border border-amber-200 bg-amber-50/60 p-4">
          <p className="flex items-center gap-1.5 text-sm font-semibold text-amber-800"><Clock className="h-4 w-4" /> Upcoming trial expirations (next 14 days)</p>
          <div className="mt-2 flex flex-wrap gap-2">
            {data.upcoming_expirations.map((r) => <span key={r.user_id} className="rounded-full bg-white px-2.5 py-1 text-xs text-zinc-700">{r.name} · {r.days_remaining}d</span>)}
          </div>
        </div>
      )}

      {/* Controls */}
      <div className="flex flex-wrap items-center gap-2">
        <div className="relative"><Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-zinc-400" /><Input data-testid="trial-search" placeholder="Search name or email" value={q} onChange={(e) => setQ(e.target.value)} className="w-56 pl-9" /></div>
        <Select value={filter} onValueChange={setFilter}>
          <SelectTrigger data-testid="trial-filter" className="w-44"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All creators</SelectItem>
            <SelectItem value="free">Free</SelectItem>
            <SelectItem value="trial">Active trial</SelectItem>
            <SelectItem value="paid">Paid Pro</SelectItem>
            <SelectItem value="complimentary">Complimentary Pro</SelectItem>
            <SelectItem value="lifetime">Lifetime Pro</SelectItem>
            <SelectItem value="expired_trial">Expired trial</SelectItem>
          </SelectContent>
        </Select>
        {selectedIds.length > 0 && (
          <>
            <Button size="sm" data-testid="bulk-grant" onClick={() => setConfirmAction({ kind: "grant", ids: selectedIds })} className="rounded-full bg-emerald-600 hover:bg-emerald-700"><Gift className="mr-1 h-3.5 w-3.5" /> Grant trial ({selectedIds.length})</Button>
            <Button size="sm" variant="outline" data-testid="bulk-revoke" onClick={() => setConfirmAction({ kind: "revoke", ids: selectedIds })} className="rounded-full border-rose-200 text-rose-600 hover:bg-rose-50"><Ban className="mr-1 h-3.5 w-3.5" /> Revoke ({selectedIds.length})</Button>
          </>
        )}
      </div>

      {/* Table */}
      <div className="overflow-x-auto rounded-2xl border border-zinc-200 bg-white">
        <table className="w-full text-sm">
          <thead className="bg-zinc-50 text-left text-xs uppercase tracking-wide text-zinc-500">
            <tr>
              <th className="w-8 p-3"></th>
              <th className="p-3">Creator</th><th className="p-3">Type</th><th className="p-3">Plan</th>
              <th className="p-3">Trial expiry</th><th className="p-3">Days</th><th className="p-3">Status</th><th className="p-3 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.user_id} data-testid={`trial-row-${r.user_id}`} className="border-t border-zinc-100">
                <td className="p-3"><input type="checkbox" data-testid={`trial-check-${r.user_id}`} checked={!!sel[r.user_id]} onChange={(e) => setSel((s) => ({ ...s, [r.user_id]: e.target.checked }))} /></td>
                <td className="p-3"><p className="font-medium text-zinc-900">{r.name}</p><p className="text-xs text-zinc-400">{r.email}</p></td>
                <td className="p-3"><span className={`rounded-full px-2 py-0.5 text-xs font-semibold ${ETYPE[r.entitlement_type]?.cls || ""}`}>{ETYPE[r.entitlement_type]?.label || r.entitlement_type}</span></td>
                <td className="p-3 text-zinc-600">{r.plan}</td>
                <td className="p-3 text-zinc-600">{r.trial_expiry ? new Date(r.trial_expiry).toLocaleDateString() : "—"}</td>
                <td className="p-3 text-zinc-600">{r.days_remaining ?? "—"}</td>
                <td className="p-3 text-zinc-600">{r.account_status}</td>
                <td className="p-3">
                  <div className="flex justify-end gap-1">
                    {["free", "expired_trial"].includes(r.entitlement_type) && (
                      <Button size="sm" variant="outline" data-testid={`grant-${r.user_id}`} disabled={busy} onClick={() => run("grant", [r.user_id])} className="rounded-full" title="Grant trial"><Gift className="h-3.5 w-3.5" /></Button>
                    )}
                    {r.entitlement_type === "trial" && (
                      <>
                        <Button size="sm" variant="outline" data-testid={`extend-${r.user_id}`} disabled={busy} onClick={() => run("adjust", [r.user_id], { days: 30 })} className="rounded-full" title="Extend 30 days"><Plus className="h-3.5 w-3.5" /></Button>
                        <Button size="sm" variant="outline" data-testid={`shorten-${r.user_id}`} disabled={busy} onClick={() => run("adjust", [r.user_id], { days: -30 })} className="rounded-full" title="Shorten 30 days"><Minus className="h-3.5 w-3.5" /></Button>
                        <Button size="sm" variant="outline" data-testid={`revoke-${r.user_id}`} disabled={busy} onClick={() => setConfirmAction({ kind: "revoke", ids: [r.user_id] })} className="rounded-full border-rose-200 text-rose-600" title="Revoke"><Ban className="h-3.5 w-3.5" /></Button>
                      </>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {rows.length === 0 && <p className="p-6 text-center text-sm text-zinc-400">No creators match.</p>}
      </div>

      {/* Audit */}
      <details className="rounded-2xl border border-zinc-200 bg-white p-4">
        <summary className="cursor-pointer text-sm font-semibold text-zinc-900">Audit history ({data.audit.length})</summary>
        <div className="mt-3 space-y-1.5 text-xs text-zinc-600">
          {data.audit.slice(0, 60).map((a) => (
            <div key={a.id} className="flex flex-wrap gap-2 border-b border-zinc-50 py-1">
              <span className="text-zinc-400">{new Date(a.timestamp).toLocaleString()}</span>
              <span className="font-medium">{a.previous_status || "—"} → {a.new_status || "—"}</span>
              <span className="text-zinc-500">{a.reason}</span>
            </div>
          ))}
        </div>
      </details>

      {/* Confirm dialog */}
      <Dialog open={!!confirmAction} onOpenChange={(o) => !o && setConfirmAction(null)}>
        <DialogContent data-testid="trial-confirm-dialog">
          <DialogHeader>
            <DialogTitle>{confirmAction?.kind === "grant" ? "Grant Founding Creator trial" : "Revoke trial"}?</DialogTitle>
            <DialogDescription>
              {confirmAction?.kind === "grant"
                ? `This grants a ${cfg.creator_trial_months}-month Creator Pro trial to ${confirmAction?.ids.length} creator(s) from today. Already-Pro creators are skipped.`
                : `This ends the trial for ${confirmAction?.ids.length} creator(s) and returns them to Free. All their data is preserved. Paid/lifetime accounts are not affected.`}
            </DialogDescription>
          </DialogHeader>
          <Button data-testid="trial-confirm-btn" disabled={busy} onClick={() => run(confirmAction.kind, confirmAction.ids)}
            className={`w-full rounded-full ${confirmAction?.kind === "grant" ? "bg-emerald-600 hover:bg-emerald-700" : "bg-rose-600 hover:bg-rose-700"}`}>
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : confirmAction?.kind === "grant" ? "Confirm grant" : "Confirm revoke"}
          </Button>
        </DialogContent>
      </Dialog>
    </div>
  );
}
